import Analytics from '@jonni/helpers/analytics';
import { bugsnag } from '@jonni/services/BugsnagService';
import {
  BusinessIndustry,
  BusinessSetupBusinessType,
  BusinessType,
  OnboardingStatus,
  useGetUserQuery,
  useSignUpMutation,
} from '@jonni/shared/graphql/appsync.full';
import { logger } from '@jonni/utils/logger';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchAuthSession } from 'aws-amplify/auth';
import { navigate } from 'gatsby';
import { useCallback, useMemo } from 'react';

export const PERSIST_KEY = 'persist:steps';

export enum VerificationFlowType {
  signIn = 'signIn',
  signUp = 'signUp',
}

export const businessTypeMapping: Record<BusinessType, BusinessSetupBusinessType> = {
  [BusinessType.MURSHE]: BusinessSetupBusinessType.MURSHE,
  [BusinessType.PATUR]: BusinessSetupBusinessType.PATUR,
};

export enum OnboardingStepScreen {
  WELCOME = 'WELCOME',
  AUTHENTICATION = 'AUTHENTICATION',
  VERIFICATION = 'VERIFICATION',
  BUSINESS_STATUS = 'BUSINESS_STATUS',
  BUSINESS_TYPE = 'BUSINESS_TYPE',
  ACCOUNTANT_EXISTENCE = 'ACCOUNTANT_EXISTENCE',
  EMPLOYEES_EXISTENCE = 'EMPLOYEES_EXISTENCE',
  BUSINESS_DETAILS = 'BUSINESS_DETAILS',
  BUSINESS_INDUSTRIES = 'BUSINESS_INDUSTRIES',
  MONTHLY_INCOME = 'MONTHLY_INCOME',
  GIFT_DETAILS = 'GIFT_DETAILS',
  BILLING_DETAILS = 'BILLING_DETAILS',
  AUTHORITIES_IDENTIFICATION_DETAILS = 'AUTHORITIES_IDENTIFICATION_DETAILS',
  AUTHORITIES_IDENTIFICATION_UPLOAD = 'AUTHORITIES_IDENTIFICATION_UPLOAD',
  BANK_DOCUMENT_UPLOAD = 'BANK_DOCUMENT_UPLOAD',
  DONE = 'DONE',
}

export interface StepsState {
  currentScreen: OnboardingStepScreen;
  currentUser: { id: string; registrationNumber: string };
  currentBusinessId: string | null;
  loading: boolean;
  businessSetup: {
    hasBusiness: boolean | null;
    businessType: BusinessType | null;
    hasAccountant: boolean | null;
    hasEmployees: boolean | null;
  };
  newBusinessDetails: {
    industry: BusinessIndustry | null;
    secondaryIndustry: BusinessIndustry | null;
    expectedMonthlyIncome: number | null;
  };
  authentication: {
    number: string;
    verificationType: string;
  };
}

export const MapStepNameToStepNumber = {
  WELCOME: 1,
  AUTHENTICATION: 1,
  VERIFICATION: 1,
  BUSINESS_STATUS: 2,
  BUSINESS_TYPE: 3,
  ACCOUNTANT_EXISTENCE: 3,
  EMPLOYEES_EXISTENCE: 3,
  BUSINESS_DETAILS: 4,
  BUSINESS_INDUSTRIES: 4,
  MONTHLY_INCOME: 4,
  GIFT_DETAILS: 5,
  BILLING_DETAILS: 5,
  AUTHORITIES_IDENTIFICATION_DETAILS: 6,
  AUTHORITIES_IDENTIFICATION_UPLOAD: 6,
  BANK_DOCUMENT_UPLOAD: 6,
  DONE: 7,
} as const;

type NavigationOptions = {
  to: OnboardingStepScreen;
  additionalState?: Partial<StepsState>;
  skipAuthCheck?: boolean;
};

export function getStepScreenFromUserOnboardingStatus(onboardingStatus: OnboardingStatus): OnboardingStepScreen {
  switch (onboardingStatus) {
    case OnboardingStatus.BUSINESS_SETUP:
      return OnboardingStepScreen.BUSINESS_STATUS;
    case OnboardingStatus.BUSINESS_DETAILS:
      return OnboardingStepScreen.BUSINESS_DETAILS;
    case OnboardingStatus.NEW_BUSINESS_DETAILS:
      return OnboardingStepScreen.BUSINESS_INDUSTRIES;
    case OnboardingStatus.GIFT_DETAILS:
      return OnboardingStepScreen.GIFT_DETAILS;
    case OnboardingStatus.BILLING_DETAILS:
      return OnboardingStepScreen.BILLING_DETAILS;
    case OnboardingStatus.AUTHORITIES_IDENTIFICATION:
      return OnboardingStepScreen.AUTHORITIES_IDENTIFICATION_DETAILS;
    case OnboardingStatus.IDENTIFICATION_UPLOAD:
      return OnboardingStepScreen.AUTHORITIES_IDENTIFICATION_UPLOAD;
    case OnboardingStatus.DONE:
      return OnboardingStepScreen.DONE;
  }
}

export function getOnboardingStatusFromStepScreen(stepScreen: OnboardingStepScreen): OnboardingStatus {
  switch (stepScreen) {
    case OnboardingStepScreen.BUSINESS_STATUS:
    case OnboardingStepScreen.BUSINESS_TYPE:
    case OnboardingStepScreen.ACCOUNTANT_EXISTENCE:
    case OnboardingStepScreen.EMPLOYEES_EXISTENCE:
      return OnboardingStatus.BUSINESS_SETUP;
    case OnboardingStepScreen.BUSINESS_DETAILS:
      return OnboardingStatus.BUSINESS_DETAILS;
    case OnboardingStepScreen.BUSINESS_INDUSTRIES:
    case OnboardingStepScreen.MONTHLY_INCOME:
      return OnboardingStatus.NEW_BUSINESS_DETAILS;
    case OnboardingStepScreen.GIFT_DETAILS:
      return OnboardingStatus.GIFT_DETAILS;
    case OnboardingStepScreen.BILLING_DETAILS:
      return OnboardingStatus.BILLING_DETAILS;
    case OnboardingStepScreen.AUTHORITIES_IDENTIFICATION_DETAILS:
      return OnboardingStatus.AUTHORITIES_IDENTIFICATION;
    case OnboardingStepScreen.AUTHORITIES_IDENTIFICATION_UPLOAD:
    case OnboardingStepScreen.BANK_DOCUMENT_UPLOAD:
      return OnboardingStatus.IDENTIFICATION_UPLOAD;
    case OnboardingStepScreen.DONE:
      return OnboardingStatus.DONE;
  }

  throw new Error(`Unhandled step screen: ${stepScreen}`);
}

export function getNextStepScreen(currentStep: OnboardingStepScreen, isNewBusiness?: boolean): OnboardingStepScreen {
  switch (currentStep) {
    case OnboardingStepScreen.WELCOME:
      return OnboardingStepScreen.AUTHENTICATION;
    case OnboardingStepScreen.AUTHENTICATION:
      return OnboardingStepScreen.VERIFICATION;
    case OnboardingStepScreen.VERIFICATION:
      return OnboardingStepScreen.BUSINESS_STATUS;
    case OnboardingStepScreen.BUSINESS_STATUS:
      return OnboardingStepScreen.BUSINESS_TYPE;
    case OnboardingStepScreen.BUSINESS_TYPE:
      return OnboardingStepScreen.ACCOUNTANT_EXISTENCE;
    case OnboardingStepScreen.ACCOUNTANT_EXISTENCE:
      return OnboardingStepScreen.EMPLOYEES_EXISTENCE;
    case OnboardingStepScreen.EMPLOYEES_EXISTENCE:
      return OnboardingStepScreen.BUSINESS_DETAILS;
    case OnboardingStepScreen.BUSINESS_DETAILS:
      if (isNewBusiness) {
        return OnboardingStepScreen.BUSINESS_INDUSTRIES;
      } else {
        return OnboardingStepScreen.GIFT_DETAILS;
      }
    case OnboardingStepScreen.BUSINESS_INDUSTRIES:
      return OnboardingStepScreen.MONTHLY_INCOME;
    case OnboardingStepScreen.MONTHLY_INCOME:
      return OnboardingStepScreen.GIFT_DETAILS;
    case OnboardingStepScreen.GIFT_DETAILS:
      return OnboardingStepScreen.BILLING_DETAILS;
    case OnboardingStepScreen.BILLING_DETAILS:
      return OnboardingStepScreen.AUTHORITIES_IDENTIFICATION_DETAILS;
    case OnboardingStepScreen.AUTHORITIES_IDENTIFICATION_DETAILS:
      return OnboardingStepScreen.AUTHORITIES_IDENTIFICATION_UPLOAD;
    case OnboardingStepScreen.AUTHORITIES_IDENTIFICATION_UPLOAD:
      return OnboardingStepScreen.BANK_DOCUMENT_UPLOAD;
    case OnboardingStepScreen.BANK_DOCUMENT_UPLOAD:
      return OnboardingStepScreen.DONE;
    case OnboardingStepScreen.DONE:
      return OnboardingStepScreen.DONE;
  }
}

/**
 * Type-safe query key for onboarding state management
 */
const ONBOARDING_QUERY_KEY = ['onboarding', 'state'] as const;

/**
 * Creates the initial state for the onboarding flow
 */
const createInitialState = (): StepsState => {
  const savedState = typeof window !== 'undefined' ? localStorage.getItem(PERSIST_KEY) : null;
  const parsedState = savedState ? JSON.parse(savedState) : null;

  return {
    currentScreen: OnboardingStepScreen.WELCOME,
    currentUser: {
      id: '',
      registrationNumber: '',
    },
    currentBusinessId: null,
    loading: true,
    businessSetup: {
      hasBusiness: null,
      businessType: null,
      hasAccountant: null,
      hasEmployees: null,
    },
    newBusinessDetails: {
      industry: null,
      secondaryIndustry: null,
      expectedMonthlyIncome: null,
    },
    authentication: {
      number: parsedState?.authentication?.number || '',
      verificationType: VerificationFlowType.signUp,
    },
  };
};

// Create a stable reference for initial data
const initialData = createInitialState();

/**
 * Storage handler for onboarding persistence
 */
const onboardingStorage = {
  load: (): StepsState => {
    if (typeof window === 'undefined') return initialData;
    try {
      const savedState = localStorage.getItem(PERSIST_KEY);
      if (!savedState) return initialData;
      const parsedState = JSON.parse(savedState);
      return { ...initialData, ...parsedState, loading: false };
    } catch (error) {
      logger.error('Failed to load onboarding state', { error });
      return initialData;
    }
  },
  save: (state: StepsState): void => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(PERSIST_KEY, JSON.stringify(state));
    } catch (error) {
      logger.error('Failed to save onboarding state', { error });
    }
  },
};

export const useOnboarding = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: signUpMutation } = useSignUpMutation();

  const { refetch: refetchUser } = useGetUserQuery(undefined, {
    enabled: false,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const {
    data: onboardingState = initialData,
    isLoading: isLoadingState,
    refetch: fetchCurrentUser,
  } = useQuery({
    queryKey: ONBOARDING_QUERY_KEY,
    queryFn: async (): Promise<StepsState> => {
      const state = onboardingStorage.load();

      if ([OnboardingStepScreen.WELCOME, OnboardingStepScreen.AUTHENTICATION].includes(state.currentScreen)) {
        navigate(`/signup/${state.currentScreen.toLowerCase().replace(/_/g, '-')}`, { replace: true });

        return {
          ...initialData,
          ...state,
          loading: false,
        };
      }

      try {
        const { identityId } = await fetchAuthSession();

        if (!identityId) {
          navigate('/signup/welcome', { replace: true });

          return {
            ...createInitialState(),
            loading: false,
          };
        }

        const data = await refetchUser();
        const userData = data?.data?.getUser;

        if (userData) {
          bugsnag.setUser(userData.id, userData.mobile, `${userData.firstName} ${userData.lastName}`);
          const currentUsersStepScreen = getStepScreenFromUserOnboardingStatus(userData.onboardingStatus);
          navigate(`/signup/${currentUsersStepScreen.toLowerCase().replace(/_/g, '-')}`, { replace: true });

          return {
            ...state,
            loading: false,
            currentScreen: currentUsersStepScreen,
            currentUser: {
              id: userData.id,
              registrationNumber: userData.idNumber || '',
            },
            currentBusinessId: userData.businesses?.[0] ? userData.businesses[0].id : '',
            authentication: {
              ...onboardingState.authentication,
              number: `${'0' + userData.mobile.slice(4)}`,
            },
          };
        } else {
          const userId = await signUpCurrentUser(identityId);

          if (userId) {
            Analytics.logOnboardingStart();

            navigate(
              `/signup/${getNextStepScreen(OnboardingStepScreen.VERIFICATION).toLowerCase().replace(/_/g, '-')}`,
              { replace: true },
            );

            return {
              ...state,
              currentScreen: getNextStepScreen(OnboardingStepScreen.VERIFICATION),
              currentUser: {
                registrationNumber: '',
                id: userId,
              },
              loading: false,
            };
          } else {
            throw new Error('signUpMutation - Received success response but no user was provided in response body');
          }
        }
      } catch (err) {
        navigate('/signup/welcome', { replace: true });

        return {
          ...createInitialState(),
          loading: false,
        };
      }
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: false,
  });

  const signUpCurrentUser = useCallback(
    async (identityId: string) => {
      try {
        const res = await signUpMutation({
          input: { identityId },
        });

        return res?.signUp?.id || null;
      } catch (err) {
        throw err;
      }
    },
    [signUpMutation],
  );

  const { mutate: batchedUpdate, isPending: isUpdating } = useMutation({
    mutationFn: async (newState: Partial<StepsState>) => {
      const currentState = queryClient.getQueryData(ONBOARDING_QUERY_KEY) as StepsState;
      const mergedState = { ...currentState, ...newState };
      onboardingStorage.save({
        ...mergedState,
      });
      return mergedState;
    },
    onMutate: async (newState) => {
      await queryClient.cancelQueries({ queryKey: ONBOARDING_QUERY_KEY });
      const previousState = queryClient.getQueryData(ONBOARDING_QUERY_KEY) as StepsState;
      queryClient.setQueryData(ONBOARDING_QUERY_KEY, (old: StepsState) => ({ ...old, ...newState }));
      return { previousState };
    },
    onError: (_, __, context) => {
      if (context?.previousState) {
        queryClient.setQueryData(ONBOARDING_QUERY_KEY, context.previousState);
      }
    },
  });

  // Memoized state updaters
  const stateUpdater = useMemo(
    () => ({
      updateState: <T extends keyof StepsState>(section: T, details: Partial<StepsState[T]>) => {
        const currentState = queryClient.getQueryData(ONBOARDING_QUERY_KEY) as StepsState;
        const currentSectionState = currentState[section];

        // Only update if there are actual changes
        // if (JSON.stringify(currentSectionState) === JSON.stringify(details)) {
        //   return;
        // }

        const newSectionState =
          typeof currentSectionState === 'object' && currentSectionState !== null
            ? { ...currentSectionState, ...details }
            : details;

        const update = {
          [section]: newSectionState,
        };

        batchedUpdate(update);
      },
      resetState: (keepAuthNumber = false) => {
        const initialState = createInitialState();
        if (keepAuthNumber && onboardingState.authentication.number) {
          initialState.authentication.number = onboardingState.authentication.number;
        }
        batchedUpdate(initialState);
      },
    }),
    [queryClient, batchedUpdate, onboardingState.authentication.number],
  );

  const navigateToStep = useCallback(
    async ({ to, additionalState, skipAuthCheck = false }: NavigationOptions) => {
      if (skipAuthCheck === false && onboardingState.currentUser.id.length === 0) {
        navigate('/signup/welcome', { replace: true });
        return;
      }

      try {
        const updates = { currentScreen: to, ...additionalState, loading: false };
        await Promise.all([
          batchedUpdate(updates),
          navigate(`/signup/${to.toLowerCase().replace(/_/g, '-')}`, { replace: true }),
        ]);
      } catch (error) {
        throw error;
      }
    },
    [onboardingState.currentUser, batchedUpdate],
  );

  const navigateToPreviousStep = useCallback(async () => {
    const { currentScreen } = onboardingState;
    const screensArray = Object.values(OnboardingStepScreen);
    const currentIndex = screensArray.indexOf(currentScreen);

    if (currentIndex <= 0) return;

    await navigateToStep({
      to: screensArray[currentIndex - 1],
      skipAuthCheck: [
        OnboardingStepScreen.WELCOME,
        OnboardingStepScreen.AUTHENTICATION,
        OnboardingStepScreen.VERIFICATION,
      ].includes(currentScreen),
    });
  }, [navigateToStep, onboardingState]);

  const validateStepBeforeSubmit = useCallback(async () => {
    const { data: userData } = await refetchUser();
    const userOnboardingStatus = userData?.getUser?.onboardingStatus;

    if (!userOnboardingStatus) return false;

    const expectedOnboardingStatus = getOnboardingStatusFromStepScreen(onboardingState.currentScreen);

    if (expectedOnboardingStatus === userOnboardingStatus) {
      return true;
    }

    const redirectStepScreen = getStepScreenFromUserOnboardingStatus(expectedOnboardingStatus);
    navigate(`/signup/${redirectStepScreen.toLowerCase().replace(/_/g, '-')}`, { replace: true });

    return false;
  }, [onboardingState.currentScreen, refetchUser]);

  return useMemo(
    () => ({
      state: onboardingState,
      isLoading: isLoadingState || isUpdating || onboardingState.loading,
      ...stateUpdater,
      navigateToStep,
      fetchCurrentUser,
      validateStepBeforeSubmit,
      navigateToPreviousStep,
      signUpCurrentUser,
    }),
    [
      onboardingState,
      isLoadingState,
      isUpdating,
      stateUpdater,
      navigateToStep,
      fetchCurrentUser,
      validateStepBeforeSubmit,
      navigateToPreviousStep,
      signUpCurrentUser,
    ],
  );
};
